// libraries
import type { ReduxStore } from '@makemydeal/dr-shared-store';

// interfaces/types
import type { StateTree } from '@makemydeal/dr-dash-types';

/**
 * Per-store, per-scope mutable state for middleware that has to remember something across dispatches
 * (sequence counters, high-water marks, request ids).
 *
 * Two keys, both load-bearing:
 *
 * - **`__realStore`** — a scoped store (Alt Deal Structure slideout) wraps the real one and re-invokes the
 *   middleware chain on every dispatch, so keying on the wrapper would reset the state constantly. A
 *   `WeakMap` also means each test's fresh store object starts clean with no teardown.
 * - **`__scopeId`** — the slideout shares its `__realStore` with the main deal, so keying on the real store
 *   alone would let slideout payments and main-deal payments advance one shared counter and supersede each
 *   other.
 *
 * Extracted because `paymentSequenceMiddleware` and `paymentRaceTelemetryMiddleware` need exactly this and
 * had two copies of the same lookup, so a fix to key resolution had to land in two places.
 * (`leaseEndpointMiddleware` and `programInvalidationMiddleware` have their own copy of this pattern in
 * `middleware/__common__/calcRequestId.ts`, predating this file — not yet consolidated onto this helper.)
 */
/** The `__scopeId` a store's state is stamped with right now, or `'__primary'` when unscoped. */
export const resolveScopeId = (store: ReduxStore<StateTree>): string =>
    (store.getState() as { __scopeId?: string } | undefined)?.__scopeId ?? '__primary';

export const createStoreScopeState = <T>(createInitial: () => T) => {
    const byStore = new WeakMap<object, Map<string, T>>();

    /**
     * `scopeIdOverride` lets a caller pin the scope it resolved at an earlier dispatch (the way
     * `paymentSequenceMiddleware` pins `PAYMENT_SEQ_LANE_META`) instead of re-deriving it from whatever
     * store is live right now. Omit it to resolve live, which is correct at first-sighting call sites where
     * there is nothing yet to pin against.
     */
    return (store: ReduxStore<StateTree>, scopeIdOverride?: string): T => {
        const realStore = ((store as unknown as { __realStore?: object }).__realStore ?? store) as object;
        const scopeId = scopeIdOverride ?? resolveScopeId(store);

        let byScope = byStore.get(realStore);
        if (!byScope) {
            byScope = new Map();
            byStore.set(realStore, byScope);
        }
        let scopeState = byScope.get(scopeId);
        if (!scopeState) {
            scopeState = createInitial();
            byScope.set(scopeId, scopeState);
        }
        return scopeState;
    };
};
